// src/services/purchaseOrderService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
  runTransaction,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  PurchaseOrder,
  PurchaseOrderFormData,
  PurchaseOrderStatus,
  PaymentStatus,
  PurchaseOrderStats,
  Supplier,
  PurchaseOrderItem,
} from './PurchaseOrder';
import { WarehouseInventoryService } from '../product/WarehouseInventoryService';
import { ProductService } from '../product/productService';
import { RawMaterialService } from './rawMaterialService';

const PO_COLLECTION = 'purchaseOrders';
const SUPPLIER_COLLECTION = 'suppliers';

export interface PaginatedPurchaseOrdersResult {
  orders: PurchaseOrder[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface PaginatedSuppliersResult {
  suppliers: Supplier[];
  totalCount: number;
  // Kept for interface compatibility — always null now.
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface SupplierStats {
  totalSuppliers: number;
  activeSuppliers: number;
  inactiveSuppliers: number;
}

export class PurchaseOrderService {
  static async generatePONumber(): Promise<string> {
    const year = new Date().getFullYear();
    const orders = await this.getPurchaseOrders();
    const yearOrders = orders.filter(po => po.createdAt.getFullYear() === year);
    const nextNum = yearOrders.length + 1;
    return `PO-${year}-${String(nextNum).padStart(5, '0')}`;
  }

  static async createPurchaseOrder(
    formData: PurchaseOrderFormData,
    userId: string,
    userName: string
  ): Promise<string> {
    try {
      const supplier = await this.getSupplier(formData.supplierId);
      if (!supplier) throw new Error('Supplier not found');

      const poNumber = await this.generatePONumber();
      const subtotal = formData.items.reduce((sum, item) => sum + item.totalPrice, 0);
      const taxRate = parseFloat(formData.taxRate) || 0;
      const taxAmount = subtotal * (taxRate / 100);
      const shippingCost = parseFloat(formData.shippingCost) || 0;
      const totalAmount = subtotal + taxAmount + shippingCost;

      const poData = {
        poNumber,
        supplierId: formData.supplierId,
        supplierName: supplier.name,
        supplierEmail: supplier.email,
        supplierPhone: supplier.phone,
        status: PurchaseOrderStatus.DRAFT,
        paymentStatus: PaymentStatus.UNPAID,
        items: formData.items.map(item => ({
          ...item,
          itemType: item.itemType || 'product',
          unit: item.unit || null,
          receivedQuantity: 0,
          warehouseId: item.warehouseId || null,
          warehouseName: item.warehouseName || null,
        })),
        subtotal,
        taxRate,
        taxAmount,
        shippingCost,
        totalAmount,
        paidAmount: 0,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        notes: formData.notes || '',
        internalNotes: formData.internalNotes || '',
        stockUpdated: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        createdByName: userName,
      };

      const docRef = await addDoc(collection(db, PO_COLLECTION), poData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating purchase order:', error);
      throw error;
    }
  }

  static async updatePurchaseOrder(
    poId: string,
    formData: PurchaseOrderFormData
  ): Promise<void> {
    try {
      const supplier = await this.getSupplier(formData.supplierId);
      if (!supplier) throw new Error('Supplier not found');

      const subtotal = formData.items.reduce((sum, item) => sum + item.totalPrice, 0);
      const taxRate = parseFloat(formData.taxRate) || 0;
      const taxAmount = subtotal * (taxRate / 100);
      const shippingCost = parseFloat(formData.shippingCost) || 0;
      const totalAmount = subtotal + taxAmount + shippingCost;

      const updateData = {
        supplierId: formData.supplierId,
        supplierName: supplier.name,
        supplierEmail: supplier.email,
        supplierPhone: supplier.phone,
        items: formData.items,
        subtotal,
        taxRate,
        taxAmount,
        shippingCost,
        totalAmount,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        notes: formData.notes || '',
        internalNotes: formData.internalNotes || '',
        updatedAt: serverTimestamp(),
      };

      const poRef = doc(db, PO_COLLECTION, poId);
      await updateDoc(poRef, updateData);
    } catch (error) {
      console.error('Error updating purchase order:', error);
      throw error;
    }
  }

  static async updatePOStatus(
    poId: string,
    status: PurchaseOrderStatus,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const updateData: any = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (status === PurchaseOrderStatus.APPROVED) {
        updateData.approvedBy = userId;
        updateData.approvedByName = userName;
        updateData.approvedAt = serverTimestamp();
      }

      const poRef = doc(db, PO_COLLECTION, poId);
      await updateDoc(poRef, updateData);
    } catch (error) {
      console.error('Error updating PO status:', error);
      throw error;
    }
  }

  static async markPOAsReceivedWithInventoryUpdate(
    poId: string,
    itemsWithWarehouses: PurchaseOrderItem[],
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const missingWarehouse = itemsWithWarehouses.find(item => !item.warehouseId);
      if (missingWarehouse) throw new Error('All items must have a warehouse assigned');

      await runTransaction(db, async (transaction) => {
        const poRef = doc(db, PO_COLLECTION, poId);
        const poDoc = await transaction.get(poRef);

        if (!poDoc.exists()) throw new Error('Purchase order not found');

        const poData = poDoc.data();
        if (poData.stockUpdated) throw new Error('Stock has already been updated for this purchase order');

        transaction.update(poRef, {
          status: PurchaseOrderStatus.RECEIVED,
          items: itemsWithWarehouses.map(item => ({
            ...item,
            receivedQuantity: item.quantity,
          })),
          receivedBy: userId,
          receivedByName: userName,
          receivedAt: serverTimestamp(),
          actualDeliveryDate: serverTimestamp(),
          stockUpdated: true,
          updatedAt: serverTimestamp(),
        });
      });

      for (const item of itemsWithWarehouses) {
        try {
          // Raw materials / other supplies are inbounded into the dedicated
          // rawMaterials inventory rather than product warehouse_inventory.
          if (item.itemType === 'raw_material') {
            await RawMaterialService.receiveRawMaterial({
              name: item.productName,
              sku: item.sku,
              unit: item.unit || '',
              quantity: item.quantity,
              unitCost: item.unitPrice,
              warehouseId: item.warehouseId!,
              warehouseName: item.warehouseName || '',
              purchaseOrderId: poId,
            });
            continue;
          }

          const product = await ProductService.getProduct(item.productId);
          if (!product) {
            console.error(`Product ${item.productId} not found, skipping inventory update`);
            continue;
          }

          const isVariant = item.variantId && item.variantId.trim() !== '';
          const variant = isVariant
            ? product.variants?.find(v => v.id === item.variantId)
            : undefined;

          // Selling price always comes from the product/variant record —
          // never from the PO's unitPrice, which is a purchase COST. Mixing
          // the two is exactly the warehouse_inventory.price ambiguity
          // described in answers.text.
          const sellingPrice = isVariant ? (variant?.price ?? 0) : product.price;
          const discount = isVariant ? variant?.discount : product.discount;

          // receiveStockWithCost adds to the existing quantity (or creates the
          // row on first receipt) and recomputes a weighted-average cost from
          // item.unitPrice — the actual cost paid on this PO — instead of
          // discarding it on every restock after the first.
          await WarehouseInventoryService.receiveStockWithCost(
            item.warehouseId!,
            item.productId,
            item.productName,
            item.quantity,
            item.unitPrice,
            10,
            100,
            item.sku,
            sellingPrice,
            isVariant ? item.variantId : undefined,
            isVariant ? item.variantName : undefined,
            discount
          );
        } catch (err) {
          console.error(`Error updating inventory for item ${item.productName}:`, err);
        }
      }
    } catch (error) {
      console.error('Error marking PO as received:', error);
      throw error;
    }
  }

  static async updatePaymentStatus(poId: string, paidAmount: number): Promise<void> {
    try {
      const po = await this.getPurchaseOrder(poId);
      if (!po) throw new Error('Purchase order not found');

      let paymentStatus: PaymentStatus;
      if (paidAmount === 0) paymentStatus = PaymentStatus.UNPAID;
      else if (paidAmount >= po.totalAmount) paymentStatus = PaymentStatus.PAID;
      else paymentStatus = PaymentStatus.PARTIAL;

      const poRef = doc(db, PO_COLLECTION, poId);
      await updateDoc(poRef, { paidAmount, paymentStatus, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }

  static async updateReceivedQuantities(
    poId: string,
    itemUpdates: { id: string; receivedQuantity: number }[]
  ): Promise<void> {
    try {
      const po = await this.getPurchaseOrder(poId);
      if (!po) throw new Error('Purchase order not found');

      const updatedItems = po.items.map(item => {
        const update = itemUpdates.find(u => u.id === item.id);
        return update ? { ...item, receivedQuantity: update.receivedQuantity } : item;
      });

      const poRef = doc(db, PO_COLLECTION, poId);
      await updateDoc(poRef, { items: updatedItems, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error updating received quantities:', error);
      throw error;
    }
  }

  /* ===================== GET PURCHASE ORDERS PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getPurchaseOrdersPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: PurchaseOrderStatus,
    paymentFilter?: PaymentStatus
  ): Promise<PaginatedPurchaseOrdersResult> {
    try {
      const orders: PurchaseOrder[] = [];
      const collectionRef = collection(db, PO_COLLECTION);

      // ── Build filter constraints (where clauses only, no ordering/paging) ──
      const filterConstraints: any[] = [];
      if (statusFilter) filterConstraints.push(where('status', '==', statusFilter));
      if (paymentFilter) filterConstraints.push(where('paymentStatus', '==', paymentFilter));

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const order: PurchaseOrder = {
          id: docSnap.id,
          poNumber: data.poNumber,
          supplierId: data.supplierId,
          supplierName: data.supplierName,
          supplierEmail: data.supplierEmail,
          supplierPhone: data.supplierPhone,
          status: data.status,
          paymentStatus: data.paymentStatus,
          items: data.items || [],
          subtotal: data.subtotal,
          taxRate: data.taxRate,
          taxAmount: data.taxAmount,
          shippingCost: data.shippingCost,
          totalAmount: data.totalAmount,
          paidAmount: data.paidAmount || 0,
          expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || undefined,
          actualDeliveryDate: data.actualDeliveryDate?.toDate() || undefined,
          notes: data.notes || '',
          internalNotes: data.internalNotes || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          createdByName: data.createdByName,
          approvedBy: data.approvedBy,
          approvedByName: data.approvedByName,
          approvedAt: data.approvedAt?.toDate() || undefined,
          receivedBy: data.receivedBy,
          receivedByName: data.receivedByName,
          receivedAt: data.receivedAt?.toDate() || undefined,
          stockUpdated: data.stockUpdated || false,
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            order.poNumber.toLowerCase().includes(searchLower) ||
            order.supplierName.toLowerCase().includes(searchLower) ||
            order.supplierEmail.toLowerCase().includes(searchLower) ||
            (order.supplierPhone && order.supplierPhone.toLowerCase().includes(searchLower)) ||
            (order.notes && order.notes.toLowerCase().includes(searchLower)) ||
            (order.createdByName && order.createdByName.toLowerCase().includes(searchLower));
          if (matchesSearch) orders.push(order);
        } else {
          orders.push(order);
        }
      });

      return {
        orders,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated purchase orders:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getPurchaseOrdersPaginated instead
   */
  static async getPurchaseOrders(): Promise<PurchaseOrder[]> {
    try {
      const q = query(collection(db, PO_COLLECTION), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || undefined,
          actualDeliveryDate: data.actualDeliveryDate?.toDate() || undefined,
          approvedAt: data.approvedAt?.toDate() || undefined,
          receivedAt: data.receivedAt?.toDate() || undefined,
          stockUpdated: data.stockUpdated || false,
        } as PurchaseOrder;
      });
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      throw error;
    }
  }

  static async getPurchaseOrder(poId: string): Promise<PurchaseOrder | null> {
    try {
      const docRef = doc(db, PO_COLLECTION, poId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || undefined,
          actualDeliveryDate: data.actualDeliveryDate?.toDate() || undefined,
          approvedAt: data.approvedAt?.toDate() || undefined,
          receivedAt: data.receivedAt?.toDate() || undefined,
          stockUpdated: data.stockUpdated || false,
        } as PurchaseOrder;
      }
      return null;
    } catch (error) {
      console.error('Error fetching purchase order:', error);
      throw error;
    }
  }

  static async deletePurchaseOrder(poId: string): Promise<void> {
    try {
      const po = await this.getPurchaseOrder(poId);
      if (po && po.stockUpdated) {
        throw new Error('Cannot delete a purchase order that has already updated stock. Please reverse the inventory changes first.');
      }
      const poRef = doc(db, PO_COLLECTION, poId);
      await deleteDoc(poRef);
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      throw error;
    }
  }

  /* ===================== GET PO STATS =====================
   * Uses getCountFromServer() for count-only fields (no document reads).
   * Financial aggregates (totalValue, totalPaid, totalOutstanding) still
   * require reading documents — they are fetched once and aggregated here.
   * ========================================================= */
  static async getPOStats(): Promise<PurchaseOrderStats> {
    try {
      const col = collection(db, PO_COLLECTION);

      const [
        totalSnap,
        draftSnap,
        pendingSnap,
        approvedSnap,
        orderedSnap,
        receivedSnap,
        cancelledSnap,
        docsSnap,
      ] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('status', '==', PurchaseOrderStatus.DRAFT))),
        getCountFromServer(query(col, where('status', '==', PurchaseOrderStatus.PENDING))),
        getCountFromServer(query(col, where('status', '==', PurchaseOrderStatus.APPROVED))),
        getCountFromServer(query(col, where('status', '==', PurchaseOrderStatus.ORDERED))),
        getCountFromServer(query(col, where('status', '==', PurchaseOrderStatus.RECEIVED))),
        getCountFromServer(query(col, where('status', '==', PurchaseOrderStatus.CANCELLED))),
        getDocs(query(col, orderBy('createdAt', 'desc'))),
      ]);

      let totalValue = 0;
      let totalPaid = 0;
      docsSnap.forEach(d => {
        totalValue += d.data().totalAmount || 0;
        totalPaid += d.data().paidAmount || 0;
      });

      return {
        total: totalSnap.data().count,
        draft: draftSnap.data().count,
        pending: pendingSnap.data().count,
        approved: approvedSnap.data().count,
        ordered: orderedSnap.data().count,
        received: receivedSnap.data().count,
        cancelled: cancelledSnap.data().count,
        totalValue,
        totalPaid,
        totalOutstanding: totalValue - totalPaid,
      };
    } catch (error) {
      console.error('Error getting PO stats:', error);
      throw error;
    }
  }

  static async getSupplierStats(): Promise<SupplierStats> {
    try {
      const collectionRef = collection(db, SUPPLIER_COLLECTION);
      const [totalSnapshot, activeSnapshot] = await Promise.all([
        getCountFromServer(collectionRef),
        getCountFromServer(query(collectionRef, where('isActive', '==', true))),
      ]);
      const totalSuppliers = totalSnapshot.data().count;
      const activeSuppliers = activeSnapshot.data().count;
      return { totalSuppliers, activeSuppliers, inactiveSuppliers: totalSuppliers - activeSuppliers };
    } catch (error) {
      console.error('Error fetching supplier stats:', error);
      throw error;
    }
  }

  /* ===================== GET SUPPLIERS PAGINATED =====================
   *
   * FIX: Same seek-based pagination pattern as getPurchaseOrdersPaginated.
   * ================================================================== */
  static async getSuppliersPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: boolean
  ): Promise<PaginatedSuppliersResult> {
    try {
      const suppliers: Supplier[] = [];
      const collectionRef = collection(db, SUPPLIER_COLLECTION);

      // ── Build filter constraints ───────────────────────────────────────────
      const filterConstraints: any[] = [];
      if (statusFilter !== undefined) filterConstraints.push(where('isActive', '==', statusFilter));

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const supplier: Supplier = {
          id: docSnap.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address || '',
          contactPerson: data.contactPerson || '',
          taxId: data.taxId || '',
          paymentTerms: data.paymentTerms || '',
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            supplier.name.toLowerCase().includes(searchLower) ||
            supplier.email.toLowerCase().includes(searchLower) ||
            (supplier.phone && supplier.phone.toLowerCase().includes(searchLower)) ||
            (supplier.contactPerson && supplier.contactPerson.toLowerCase().includes(searchLower)) ||
            (supplier.taxId && supplier.taxId.toLowerCase().includes(searchLower)) ||
            (supplier.address && supplier.address.toLowerCase().includes(searchLower));
          if (matchesSearch) suppliers.push(supplier);
        } else {
          suppliers.push(supplier);
        }
      });

      return {
        suppliers,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated suppliers:', error);
      throw error;
    }
  }

  static async createSupplier(
    supplier: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, SUPPLIER_COLLECTION), {
        ...supplier,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  }

  static async getSupplier(supplierId: string): Promise<Supplier | null> {
    try {
      const docRef = doc(db, SUPPLIER_COLLECTION, supplierId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Supplier;
      }
      return null;
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error;
    }
  }

  static async updateSupplier(
    supplierId: string,
    supplierData: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<void> {
    try {
      const supplierRef = doc(db, SUPPLIER_COLLECTION, supplierId);
      await updateDoc(supplierRef, { ...supplierData, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  }

  static async deleteSupplier(supplierId: string): Promise<void> {
    try {
      const supplierRef = doc(db, SUPPLIER_COLLECTION, supplierId);
      await deleteDoc(supplierRef);
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getSuppliersPaginated instead
   */
  static async getSuppliers(): Promise<Supplier[]> {
    try {
      const q = query(
        collection(db, SUPPLIER_COLLECTION),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as Supplier));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      try {
        const q = query(collection(db, SUPPLIER_COLLECTION), where('isActive', '==', true));
        const snapshot = await getDocs(q);
        const suppliers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as Supplier));
        return suppliers.sort((a, b) => a.name.localeCompare(b.name));
      } catch (fallbackError) {
        console.error('Error in fallback query:', fallbackError);
        throw fallbackError;
      }
    }
  }

  /**
   * @deprecated Use getSuppliersPaginated instead
   */
  static async getAllSuppliers(): Promise<Supplier[]> {
    try {
      const q = query(collection(db, SUPPLIER_COLLECTION), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as Supplier));
    } catch (error) {
      console.error('Error fetching all suppliers:', error);
      try {
        const snapshot = await getDocs(collection(db, SUPPLIER_COLLECTION));
        const suppliers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as Supplier));
        return suppliers.sort((a, b) => a.name.localeCompare(b.name));
      } catch (fallbackError) {
        console.error('Error in fallback query:', fallbackError);
        throw fallbackError;
      }
    }
  }
}